import { ScrollArea } from '@radix-ui/react-scroll-area'

import { useMemo, useState } from 'react'
import { useBackupFilesQuery } from '../hooks/useBackupFilesQuery'
import DownloadButton from './BackupDownloadButton'
import MobileBackupFileCard from './MobileBackupFileCard'
import { TBody, TD, TH, THead, TR, Table } from '@/components/custom/table'
import { formatDate } from '@/utils/timeZoneUtils'
import { Badge } from '@/components/custom/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Container } from '@/components/Container'
import { PaginationWrapper } from '@/components/custom/PaginationWrapper'
import { usePagination } from '@/hooks'

export const BackupFiles = () => {
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false)
  const { data, isLoading } = useBackupFilesQuery()
  const files = useMemo(() => {
    if (!data) return []
    if (data.files.length === 0) return []
    // Sort by createdTime descending (newest first)
    const sorted = [...data.files].sort((a, b) => {
      const aTime = a.createdTime ? new Date(a.createdTime).getTime() : 0
      const bTime = b.createdTime ? new Date(b.createdTime).getTime() : 0
      return bTime - aTime
    })
    if (showOnlyAvailable) return sorted.filter((f) => f.available)
    return sorted
  }, [data, showOnlyAvailable])

  const { page, pageCount, rows, setPage } = usePagination({
    data: files,
    pageSize: 10,
  })

  if (isLoading) {
    return (
      <Container>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700 mx-auto mb-4" />
      </Container>
    )
  }

  return (
    <div className="lg:space-y-2 space-y-1 flex-1">
      <div className="flex items-center justify-end">
        <Label className="mr-2 text-sm">Show Downloadable Only</Label>
        <Switch
          checked={showOnlyAvailable}
          onCheckedChange={setShowOnlyAvailable}
        />
      </div>

      {/* Desktop Table */}
      <ScrollArea className="flex">
        <div className="hidden lg:block overflow-hidden rounded-md border bg-white w-full">
          <Table>
            <THead>
              <TR>
                <TH>File Name</TH>
                <TH>Created</TH>
                <TH>Status</TH>
                <TH>Actions</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((file) => {
                return (
                  <TR key={file.id}>
                    <TD>{file.name}</TD>
                    <TD>
                      {file.createdTime
                        ? formatDate(file.createdTime, 'DD/MM/YYYY hh:mm A')
                        : '-'}
                    </TD>
                    <TD>
                      {file.available ? (
                        <Badge color="green" variant="soft" size="sm">
                          Available
                        </Badge>
                      ) : (
                        <Badge color="gray" variant="soft" size="sm">
                          Unavailable
                        </Badge>
                      )}
                    </TD>
                    <TD>
                      {file.available ? (
                        <DownloadButton fileId={file.id} />
                      ) : (
                        <span>-</span>
                      )}
                    </TD>
                  </TR>
                )
              })}
              {files.length === 0 && (
                <TR>
                  <TD className="p-6 text-center text-neutral-500" colSpan={6}>
                    No backups found
                  </TD>
                </TR>
              )}
            </TBody>
          </Table>
        </div>
      </ScrollArea>

      {/* Mobile Card List */}
      <div className="block lg:hidden space-y-4 overflow-hidden">
        {files.length === 0 ? (
          <div className="p-6 text-center text-neutral-500 border rounded-md bg-white">
            No results
          </div>
        ) : (
          <ScrollArea className="flex h-full">
            <div className="flex-1 space-y-2 pb-4">
              {files.map((file) => {
                return <MobileBackupFileCard key={file.id} file={file} />
              })}
            </div>
          </ScrollArea>
        )}
      </div>

      {/* Desktop Pagination */}
      <div className="hidden lg:block">
        <PaginationWrapper
          pageCount={pageCount}
          currentPage={page}
          onPageChange={setPage}
        />
      </div>
    </div>
  )
}
export default BackupFiles
